import { CellContext } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { NumericFormat } from 'react-number-format';
import clsx from 'clsx';
import AppCountDown, { AppCountDownTick, IFormatOptions } from 'src/components/AppCountDown/AppCountDown';
import DateTimeText from 'src/components/DateTimeText/DateTimeText';
import appMoment from 'src/helpers/app-moment';
import { FlashDiscountPeriodTypeResponse, IAcceptedFlashProductListItem, FlashProductTransition } from '../services/models/flash-discount';

interface IProps {
  period: FlashDiscountPeriodTypeResponse & { colors: [string, string] };
  cellContext: CellContext<IAcceptedFlashProductListItem, unknown>;
}

const CustomInput: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => {
  return <input {...props} />;
};

export default function AcceptedFlashProductsPeriodCell(props: IProps) {
  const { period, cellContext: ctx } = props;

  const periodData = (ctx?.row?.original?.periodInfo || []).find((value) => value?.periodTypeId === period?.id);

  const isWaiting = useMemo(() => {
    return appMoment(periodData?.startAt).isAfter(appMoment());
  }, [periodData?.startAt]);

  const isActive = useMemo(() => {
    return appMoment(periodData?.startAt).isBefore(appMoment()) && appMoment(periodData?.endAt).isAfter(appMoment());
  }, [periodData?.startAt, periodData?.endAt]);

  const isFinished = useMemo(() => {
    return appMoment(periodData?.endAt).isBefore(appMoment());
  }, [periodData?.endAt]);

  const countDownFormatFn = useCallback((opts: IFormatOptions) => {
    // Bir günden büyükse, gün ve saati göster
    if (!opts.helpers.lessThan.day) return `{[D] gün} {[H] saat}`;
    // Bir saatten büyükse, saati ve dakikayı göster
    if (!opts.helpers.lessThan.hour) return `{[H] saat} {[m] dakika}`;
    // Bir dakikadan büyükse, dakikayı ve saniyeyi göster
    else if (!opts.helpers.lessThan.minute) return `{[m] dakika} {[s] saniye}`;
    // Bir saniyeden büyükse, saniyeyi göster
    else if (!opts.helpers.lessThan.second) return `{[s] saniye}`;

    return 'Başladı';
  }, []);

  const countDownTickFn = useCallback((opts: IFormatOptions) => {
    // Bir günden büyükse timer ekleme
    if (!opts.helpers.lessThan.day) return false;
    // Bir saatten büyükse dakikada bir çalışacak timer ekle
    if (!opts.helpers.lessThan.hour) return AppCountDownTick.MINUTES;
    // Diğer durumlarda ve kalan süre 0'dan büyükse saniyede bir çalışacak timer ekle
    if (opts.remainingTimeMs > 0) return AppCountDownTick.SECONDS;
    return false;
  }, []);

  const transition = useMemo<FlashProductTransition>(() => {
    if (isWaiting) return FlashProductTransition.waiting;
    if (isActive) return FlashProductTransition.active;
    if (isFinished) return FlashProductTransition.finished;
    return FlashProductTransition.waiting;
  }, [isWaiting, isActive, isFinished]);

  if (!periodData) return <span className="text-muted">-</span>;
  return (
    <div className="d-flex flex-column gap-4 justify-content-center">
      {/** Date Range */}
      <div
        className="d-flex gap-2 w-fit fw-bold"
        style={{
          backgroundColor: period?.colors?.[0],
          padding: '6px 8px',
          borderRadius: '4px',
          color: period?.colors?.[1],
        }}
      >
        <DateTimeText value={appMoment.parseZone(periodData.startAt)} />
        <span>-</span>
        <DateTimeText value={appMoment.parseZone(periodData.endAt)} />
      </div>

      {/** Remaining Time */}
      <div className="d-flex gap-2 mb-4">
        <div className="d-flex flex-column gap-2">
          {/** Başlamasına kalan süre */}
          <div className="d-flex gap-2">
            <strong>Başlamasına Kalan Süre:</strong>

            {[FlashProductTransition.waiting, FlashProductTransition.active].includes(transition) ? (
              <AppCountDown tooltip={false} endDate={periodData?.startAt} expiredText="Başladı" format={countDownFormatFn} tick={countDownTickFn} />
            ) : null}

            {transition === FlashProductTransition.finished ? <span>-</span> : null}
          </div>

          {/** Bitişine kalan süre */}
          <div className="d-flex gap-2">
            <strong>Bitişine Kalan Süre:</strong>

            {[FlashProductTransition.waiting, FlashProductTransition.active].includes(transition) ? (
              <AppCountDown tooltip={false} endDate={periodData?.endAt} expiredText="Sona erdi" format={countDownFormatFn} tick={countDownTickFn} />
            ) : null}

            {transition === FlashProductTransition.finished ? <span>-</span> : null}
          </div>
        </div>
      </div>

      {/** Price Input */}
      <div className="d-flex flex-column gap-2" style={{ width: 280 }}>
        <NumericFormat
          value={ctx.row.original.periodInfo.find((value) => value?.periodTypeId === period?.id)?.acceptedPrice}
          min={0}
          suffix={' ₺'}
          thousandSeparator="."
          decimalSeparator=","
          customInput={CustomInput}
          allowNegative={false}
          className={clsx('form-control form-control-sm', {})}
          disabled
        />
      </div>
    </div>
  );
}
